import type { JSX, VoidComponent } from "solid-js";
import { createMemo, createSignal, For, mergeProps, Show, untrack } from "solid-js";
import posthog from "posthog-js";
import { useTranslation } from "../../../state/i18n";
import { useCurrentModal, useModalManager } from "../../../components/modal/ModalManager";
import type { InferOutput } from "valibot";
import { nonOptional, object, picklist } from "valibot";
import Wizard from "../../../components/modal/Wizard";
import { createForm } from "../../../components/form/createForm";
import { Form } from "../../../components/form/Form";
import { errorTranslation, optionalTextValue, requireText } from "../../../utils/validation/base";
import { MAX_CHARACTERS_MD, MAX_CHARACTERS_SM } from "../../../constants";
import { TextInput } from "../../../components/inputs/TextInput";
import TextAreaInput from "../../../components/inputs/TextAreaInput";
import { SingleSelectInput } from "../../../components/menu/Select";
import { Checkbox as ArkCheckbox } from "@ark-ui/solid";
import { IconTile } from "../../../components/icon/IconTile";
import { keyFromSelector } from "i18next";
import Text from "../../../components/text/Text";
import UserService from "../../../services/UserService";
import { useCustomerState } from "../../../state/customer";
import { isBoolean, isEmpty, isNil } from "lodash-es";
import type { Role } from "../../../state/role";
import type { PermissionType } from "../../../state/Permission";
import { PERMISSION_CATEGORY_TRANSLATION_KEYS, permissionsForScope } from "../../../state/Permission";
import { CheckboxIndicator } from "../../../components/checkbox/Checkbox.tsx";
import GenericFailureModal from "../../GenericFailureModal.tsx";
import { POSTHOG_EVENTS } from "../../../services/PostHogService";

const upsertRoleSchema = object({
  name: requireText(MAX_CHARACTERS_SM),
  description: optionalTextValue(MAX_CHARACTERS_MD),
  scope: nonOptional(
    picklist(["CUSTOMER", "FOLDER", "FORM"]),
    errorTranslation(keyFromSelector(($) => $.VALIDATION_NO_SELECTION)),
  ),
});
type UpsertRoleSchemaType = InferOutput<typeof upsertRoleSchema>;

type UpsertRoleModalProps = {
  role?: Role;
};
const UpsertRoleModal: VoidComponent<UpsertRoleModalProps> = (props) => {
  const { replace } = useModalManager();
  const { close } = useCurrentModal();
  const customer = useCustomerState();

  const role = untrack(() => props.role);

  const type = isNil(role) ? "CREATE" : "EDIT";
  const initialValue = createMemo(() => ({
    name: role?.name ?? undefined,
    description: role?.description ?? undefined,
    scope: !isNil(role?.scope) && role.scope !== "MIXED" ? role.scope : undefined,
  }));

  const upsertRoleMutation = isNil(role)
    ? UserService.createRole(customer.customerId)
    : UserService.updateRole(customer.customerId, role.id);

  return (
    <MetaStep
      type={type}
      initialValue={initialValue()}
      onSubmit={({ name, description, scope }) => {
        replace({
          content: () => (
            <PermissionsStep
              type={type}
              scope={scope}
              selectedPermissions={role?.permissions ?? []}
              onSubmit={(permissions: PermissionType[]) =>
                upsertRoleMutation
                  .mutateAsync({ name, description, permissions })
                  .then(() => {
                    if (type === "CREATE") {
                      posthog.capture(POSTHOG_EVENTS.ROLE_CREATED);
                    }
                    close();
                  })
                  .catch((err) =>
                    replace({
                      content: () => <GenericFailureModal error={err} />,
                    }),
                  )
              }
            />
          ),
        });
      }}
    />
  );
};

type MetaStepProps = {
  type: "CREATE" | "EDIT";
  initialValue: {
    name?: string;
    description?: string;
    scope?: UpsertRoleSchemaType["scope"];
  };
  onSubmit: (value: UpsertRoleSchemaType) => void;
};
const MetaStep: VoidComponent<MetaStepProps> = (props) => {
  const { t } = useTranslation();

  const form = createForm<InferOutput<typeof upsertRoleSchema>>(
    upsertRoleSchema,
    ({ name, description, scope }) => {
      props.onSubmit({ name, description, scope });
    },
    props.initialValue,
  );

  return (
    <Wizard
      title={t(($) => (props.type === "CREATE" ? $.CREATE_ROLE_WIZARD_STEP_1_TITLE : $.EDIT_ROLE_WIZARD_STEP_1_TITLE))}
      content={() => (
        <div class="px-6 pt-4">
          <Form form={form}>
            {({ value, updateValue, error }) => (
              <div class="flex flex-col gap-6">
                <TextInput
                  label={t(($) => $.ROLE_NAME_INPUT_LABEL)}
                  value={value("name")}
                  required
                  onBlur={(e) => updateValue("name", e.target.value)}
                  onInput={(e) => updateValue("name", e.target.value)}
                  errorMessage={error("name")}
                />

                <TextAreaInput
                  label={t(($) => $.ROLE_DESCRIPTION_INPUT_LABEL)}
                  maxLength={MAX_CHARACTERS_MD}
                  value={value("description")}
                  onBlur={(e) => updateValue("description", e.target.value)}
                  onInput={(e) => updateValue("description", e.target.value)}
                  errorMessage={error("description")}
                />

                <Show when={props.type === "CREATE"}>
                  <SingleSelectInput
                    label={t(($) => $.ROLE_SCOPE_INPUT_LABEL)}
                    required
                    value={value("scope")}
                    options={[
                      { variant: "text", value: "CUSTOMER", label: t(($) => $.ROLE_SCOPE_CUSTOMER) },
                      { variant: "text", value: "FOLDER", label: t(($) => $.ROLE_SCOPE_FOLDER) },
                      { variant: "text", value: "FORM", label: t(($) => $.ROLE_SCOPE_FORM) },
                    ]}
                    sameWidth
                    errorMessage={error("scope")}
                    onSelect={(value) => updateValue("scope", value)}
                  />
                </Show>
              </div>
            )}
          </Form>
        </div>
      )}
      footer={{
        confirm: {
          label: keyFromSelector(($) =>
            props.type === "CREATE"
              ? $.CREATE_ROLE_WIZARD_STEP_1_ACTIONS_CONFIRM_LABEL
              : $.EDIT_ROLE_WIZARD_STEP_1_ACTIONS_CONFIRM_LABEL,
          ),
          action: () => form.submit(),
          disabled: form.submitDisabled(),
        },
        showCloseButton: true,
      }}
    />
  );
};

type PermissionsStepProps = {
  type: "CREATE" | "EDIT";
  scope: UpsertRoleSchemaType["scope"];
  selectedPermissions: PermissionType[];
  onSubmit: (permissions: PermissionType[]) => Promise<void>;
};
const PermissionsStep: VoidComponent<PermissionsStepProps> = (props) => {
  const { t } = useTranslation();
  const [selectedPermissions, setSelectedPermissions] = createSignal<PermissionType[]>(
    untrack(() => props.selectedPermissions),
  );

  return (
    <Wizard
      title={t(($) => (props.type === "CREATE" ? $.CREATE_ROLE_WIZARD_STEP_2_TITLE : $.EDIT_ROLE_WIZARD_STEP_2_TITLE))}
      content={() => {
        const permissionsByCategory = createMemo(() =>
          permissionsForScope(props.scope)
            // Do not allow 'SYSTEM' or 'LEGACY' permissions to be selected
            .filter(({ category }) => category !== "SYSTEM" && category !== "LEGACY"),
        );

        return (
          <div class="flex flex-col gap-6 px-1 pt-4">
            <For each={permissionsByCategory()}>
              {(categoryGroup) => (
                <div>
                  <h3
                    id={`permission-category-${categoryGroup.category}`}
                    class="px-3 pt-2.5 pb-0.5 text-header-md text-secondary"
                  >
                    {t(PERMISSION_CATEGORY_TRANSLATION_KEYS[categoryGroup.category])}
                  </h3>
                  <ul aria-labelledby={`permission-category-${categoryGroup.category}`}>
                    <For each={categoryGroup.permissions}>
                      {(definition) => {
                        const isDisabled = createMemo(
                          () =>
                            selectedPermissions().includes("MANAGE_FORMS") &&
                            (definition.type === "CREATE_FORM" || definition.type === "CREATE_FOLDER"),
                        );
                        const isSelected = createMemo(
                          () =>
                            selectedPermissions().includes(definition.type) ||
                            (selectedPermissions().includes("MANAGE_FORMS") &&
                              (definition.type === "CREATE_FORM" || definition.type === "CREATE_FOLDER")),
                        );

                        return (
                          <li>
                            <Checkbox
                              class="px-3 py-2.5"
                              checked={isSelected()}
                              disabled={isDisabled()}
                              setChecked={(checked) => {
                                setSelectedPermissions((prev) =>
                                  checked
                                    ? [...prev, definition.type]
                                    : prev.filter((type) => type !== definition.type),
                                );
                              }}
                              content={() => (
                                <div class="flex min-w-0 items-center gap-1.5">
                                  <IconTile icon={definition.icon} size="sm" border disabled={isDisabled()} />
                                  <div class="flex min-w-0 flex-col">
                                    <Text
                                      truncate
                                      text={t(definition.name)}
                                      class="text-base-medium"
                                      classList={{
                                        "text-primary": !isDisabled(),
                                        "text-disabled": isDisabled(),
                                      }}
                                    />
                                    <Text
                                      truncate={2}
                                      text={t(definition.description)}
                                      class="text-base-regular"
                                      classList={{
                                        "text-secondary group-checked:text-primary": !isDisabled(),
                                        "text-disabled": isDisabled(),
                                      }}
                                    />
                                  </div>
                                </div>
                              )}
                            />
                          </li>
                        );
                      }}
                    </For>
                  </ul>
                </div>
              )}
            </For>
          </div>
        );
      }}
      footer={{
        confirm: {
          label: keyFromSelector(($) =>
            props.type === "CREATE"
              ? $.CREATE_ROLE_WIZARD_STEP_2_ACTIONS_CONFIRM_LABEL
              : $.EDIT_ROLE_WIZARD_STEP_2_ACTIONS_CONFIRM_LABEL,
          ),
          action: () => props.onSubmit(selectedPermissions()),
          disabled: isEmpty(selectedPermissions()),
        },
        showCloseButton: true,
      }}
    />
  );
};

type CheckboxProps = {
  checked: boolean;
  setChecked: (checked: boolean) => void;
  disabled?: boolean;
  content: () => JSX.Element;
  class?: string;
};
const Checkbox: VoidComponent<CheckboxProps> = (rawProps) => {
  const props = mergeProps({ disabled: false }, rawProps);

  return (
    <ArkCheckbox.Root
      checked={props.checked}
      onCheckedChange={(e) => {
        if (isBoolean(e.checked)) {
          props.setChecked(e.checked);
        }
      }}
      class={`group relative flex w-full checked:bg-surface-active-brand ${props.class}`}
      classList={{
        "cursor-pointer hover:bg-surface-hover focussed:bg-surface-hover": !props.disabled,
      }}
      disabled={props.disabled}
    >
      <div class="flex w-full min-w-0 shrink items-center justify-between gap-3">
        <ArkCheckbox.Label asChild={props.content} />
        <CheckboxIndicator />
      </div>
      <ArkCheckbox.HiddenInput />
    </ArkCheckbox.Root>
  );
};

export default UpsertRoleModal;
